<template>
  <div>
    <WebhookUsageHelp />

    <v-toolbar elevation="0" class="mb-1 webhook-integrations-toolbar px-2" rounded>
      <WebhookIntegrationDialog
        v-if="$canControlTenantConfig()"
        :node-id="accessPointId"
        :integration-types="integrationTypes"
        @saved="onIntegrationSaved"
      >
        <v-btn elevation="0" class="bg-primary">
          <v-icon>mdi-webhook</v-icon>
          <span class="hidden-sm-and-down">{{ $t('routes.webhookIntegrations.createIntegration') }}</span>
        </v-btn>
      </WebhookIntegrationDialog>

      <v-text-field v-model="searchQuery" :label="$t('routes.webhookIntegrations.searchIntegrations')" variant="solo-filled" flat hide-details append-inner-icon="mdi-magnify" class="ml-2" />
      <v-select v-model="typeFilter" :items="typeFilterItems" :label="$t('routes.webhookIntegrations.type')" item-title="label" item-value="id" variant="solo-filled" flat hide-details clearable class="ml-2" style="max-width: 220px;" />
      <v-select v-model="stateFilter" :items="stateFilterItems" :label="$t('routes.webhookIntegrations.status')" variant="solo-filled" flat hide-details clearable class="ml-2" style="max-width: 180px;" />
    </v-toolbar>

    <v-row><v-col><WebhookIntegrationsListView :integrations="filteredIntegrations" :integration-types="integrationTypes" :loading="loading" @edit="onEditIntegration" @delete="onDeleteIntegration" @state="onUpdateIntegrationState" @view="onViewIntegration" /></v-col></v-row>

    <WebhookIntegrationDialog v-if="editingIntegration" v-model="showEditDialog" :integration="editingIntegration" :node-id="accessPointId" :integration-types="integrationTypes" @saved="onIntegrationUpdated" />
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useWebhookIntegrationStore } from '@/stores/WebhookIntegrationStore.js';
import { getWebhookApiErrorMessage } from '@/routes/console/webhook-integrations/WebhookApiError.js';
import WebhookUsageHelp from '@/routes/console/webhook-integrations/components/WebhookUsageHelp.vue';
import WebhookIntegrationDialog from './components/dialogs/WebhookIntegrationDialog.vue';
import WebhookIntegrationsListView from './components/WebhookIntegrationsListView.vue';
import { INTEGRATION_STATES } from './WebhookConstants.js';

export default {
  name: 'WebhookIntegrationsIndex',
  components: { WebhookIntegrationDialog, WebhookIntegrationsListView, WebhookUsageHelp },
  data() {
    return { searchQuery: '', typeFilter: null, stateFilter: null, loading: true, editingIntegration: null, showEditDialog: false };
  },
  created() {
    if (!this.$canControlTenantConfig()) this.$router.push('/console/index');
  },
  mounted() { this.fetchData(); },
  computed: {
    accessPointId() { return useAuthStore().rootNodeId; },
    integrations() { return useWebhookIntegrationStore().getIntegrations; },
    integrationTypes() { return useWebhookIntegrationStore().getIntegrationTypes; },
    stateFilterItems() { return INTEGRATION_STATES; },
    typeFilterItems() { return this.integrationTypes; },
    filteredIntegrations() {
      const query = this.searchQuery.toLowerCase();
      return this.integrations.filter((integration) => {
        const matchesQuery = !query || integration.name?.toLowerCase().includes(query) || integration.description?.toLowerCase().includes(query);
        const matchesType = !this.typeFilter || integration.type === this.typeFilter;
        const matchesState = !this.stateFilter || integration.state === this.stateFilter;
        return matchesQuery && matchesType && matchesState;
      });
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const store = useWebhookIntegrationStore();
        await Promise.all([
          store.fetchIntegrationTypes({ nodeId: this.accessPointId }),
          store.fetchIntegrations({ nodeId: this.accessPointId }),
        ]);
      } catch (error) {
        console.error('Error fetching webhook integrations', error);
        this.$showMessage(this.$t('routes.webhookIntegrations.fetchError'), { type: 'error' });
      } finally { this.loading = false; }
    },
    onIntegrationSaved() { this.$showMessage(this.$t('routes.webhookIntegrations.integrationCreated')); },
    onEditIntegration(integration) { this.editingIntegration = integration; this.showEditDialog = true; },
    onIntegrationUpdated() { this.showEditDialog = false; this.editingIntegration = null; this.$showMessage(this.$t('routes.webhookIntegrations.integrationUpdated')); },
    onViewIntegration(integration) { this.$router.push(`/console/webhook-integrations/${integration.id}`); },
    async onDeleteIntegration(integration) {
      try {
        await useWebhookIntegrationStore().deleteIntegration({ nodeId: this.accessPointId, integrationId: integration.id });
        this.$showMessage(this.$t('routes.webhookIntegrations.integrationDeleted'));
      } catch (error) {
        this.$showMessage(getWebhookApiErrorMessage(error, this.$t('routes.webhookIntegrations.deleteError')), { type: 'error' });
      }
    },
    async onUpdateIntegrationState({ integration, state }) {
      try {
        await useWebhookIntegrationStore().updateIntegrationState({ nodeId: this.accessPointId, integrationId: integration.id, state });
        this.$showMessage(this.$t('routes.webhookIntegrations.integrationUpdated'));
      } catch (error) {
        this.$showMessage(getWebhookApiErrorMessage(error, this.$t('routes.webhookIntegrations.updateError')), { type: 'error' });
      }
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.v-theme--dark).webhook-integrations-toolbar { background-color: #272727 !important; }
:deep(.v-theme--light).webhook-integrations-toolbar { background-color: white !important; }
</style>
